import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { withStyles } from '@material-ui/styles';
import intl from 'react-intl-universal';
import moment from 'moment';
import Button from 'components/button';
import DateInput from 'components/date-input';
import Pagination from 'components/pagination';
import LoadingCircularProgress from 'components/loading-circular-progress';
import { getHistoricoExecucoes, cancelarIntegracao, getStatusAtualIntegracao } from 'pages/master-data/configurations/integrations/integrations.service';
import { formatErrorMessage } from 'utils/handle-error';
import { profileAdminOrMasterData } from 'services/permissions/getPermissions';
import { CheckIcon, UnCheckIcon } from 'components/icons/icn-index.icon';
import IconRefresh from 'components/icons/icn-refresh.icon';
import CloseIcon from 'components/icons/icn-close.icon';
import * as S from './styled';

const PAGE_SIZE = 20;
const POLLING_INTERVAL_MS = 5000;

const StyledDialogContent = withStyles(() => ({
  root: {
    padding: '0 24px 24px',
    overflowX: 'hidden',
  },
}))(DialogContent);

const formatDuration = (ms) => {
  if (ms == null) return '-';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

// Verifica se um item de execução foi cancelado (tem log com Nivel === 'Cancel')
const isCanceled = (item) =>
  (item?.logs ?? []).some((log) => (log.nivel ?? log.Nivel ?? '').toLowerCase() === 'cancel');

// Verifica se um item de execução possui logs com nível 'Warn'
const hasWarnLogs = (item) =>
  (item?.logs ?? []).some((log) => (log.nivel ?? log.Nivel ?? '').toLowerCase() === 'warn');

// Normaliza campos suportando camelCase (nova API) e PascalCase (API antiga)
const normalizeItem = (item) => ({
  id: item.id ?? item.Id,
  dataInicio: item.dataInicio ?? item.DataInicio ?? null,
  dataFim: item.dataFim ?? item.DataFim ?? null,
  duracaoMs: item.duracaoMs ?? item.DuracaoMs ?? null,
  erro: item.erro ?? item.Erro ?? false,
  mensagemErro: item.mensagemErro ?? item.MensagemErro ?? null,
  stackTrace: item.stackTrace ?? item.StackTrace ?? null,
  logs: (item.logs ?? item.Logs ?? []).map((log) => ({
    id: log.id ?? log.Id,
    data: log.data ?? log.Data ?? null,
    nivel: log.nivel ?? log.Nivel ?? '',
    mensagem: log.mensagem ?? log.Mensagem ?? '',
    percentual: log.percentual ?? log.Percentual ?? null,
  })),
});

const ModalHistoricoExecucoes = ({ open, onClose, integracao, onFeedback }) => {
  const [historico, setHistorico] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos'); // 'todos' | 'sucesso' | 'erro'
  const [expandedId, setExpandedId] = useState(null);
  const [statusAtual, setStatusAtual] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const pollingRef = useRef(null);

  const isAdmin = profileAdminOrMasterData();

  // Normaliza o tipoIntegracao suportando camelCase (nova API) e PascalCase (API antiga)
  const tipoIntegracao = integracao?.tipoIntegracao ?? integracao?.TipoIntegracao;
  const descricaoTipoIntegracao = integracao?.descricaoTipoIntegracao ?? integracao?.DescricaoTipoIntegracao;

  const buscarHistorico = useCallback(async (page = 1) => {
    if (!integracao || tipoIntegracao == null) return;
    setIsFetching(true);
    try {
      const data = await getHistoricoExecucoes({
        tipoIntegracao,
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
        pagina: page,
        tamanhoPagina: PAGE_SIZE,
        incluirLogs: true,
      });
      const rawList = Array.isArray(data) ? data : (data?.items ?? data?.Items ?? []);
      const lista = rawList.map(normalizeItem);
      const total = data?.total ?? data?.Total ?? lista.length;
      setHistorico(lista);
      setTotalRegistros(total);
      setPagina(page);
    } catch (e) {
      onFeedback(formatErrorMessage(e));
    }
    setIsFetching(false);
  }, [integracao, tipoIntegracao, dataInicio, dataFim, onFeedback]);

  const pararPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const buscarStatusAtual = useCallback(async () => {
    if (tipoIntegracao == null) return;
    try {
      const raw = await getStatusAtualIntegracao(tipoIntegracao);
      // A API retorna { Status, DescricaoStatus, DataProximaExecucao, ExecucaoAtual }
      // Status === 4 significa "Em Execução" — só nesse caso o banner deve aparecer
      const statusCode = raw?.Status ?? raw?.status ?? null;
      const execucaoAtual = raw?.ExecucaoAtual ?? raw?.execucaoAtual ?? null;
      const STATUS_EM_EXECUCAO_API = 4;

      if (statusCode === STATUS_EM_EXECUCAO_API && execucaoAtual) {
        const status = normalizeItem(execucaoAtual);
        setStatusAtual(status);
        if (status.dataFim) {
          // job finalizou — parar polling e recarregar histórico
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setCancelando(false);
          buscarHistorico(1);
        }
      } else {
        // Não está em execução (agendado, standby, parado, etc.) — limpar status
        setStatusAtual(null);
        pararPolling();
      }
    } catch {
      // silencioso — status-atual pode retornar 404 se não estiver rodando
      setStatusAtual(null);
    }
  }, [tipoIntegracao, buscarHistorico, pararPolling]);

  const iniciarPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(buscarStatusAtual, POLLING_INTERVAL_MS);
  }, [buscarStatusAtual]);

  useEffect(() => {
    if (open && integracao) {
      buscarHistorico(1);
      buscarStatusAtual();
    }
    return () => pararPolling();
  }, [open, integracao]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iniciar polling se job estiver em execução
  useEffect(() => {
    if (statusAtual && !statusAtual.dataFim) {
      iniciarPolling();
    } else {
      pararPolling();
    }
  }, [statusAtual, iniciarPolling, pararPolling]);

  const handleCancelar = async () => {
    if (tipoIntegracao == null) return;
    setCancelando(true);
    try {
      await cancelarIntegracao(tipoIntegracao);
      onFeedback(intl.get('master-data.configurations.integrations.cancelRequested'));
      iniciarPolling();
    } catch (e) {
      setCancelando(false);
      onFeedback(formatErrorMessage(e));
    }
  };

  const handleFiltrar = () => {
    buscarHistorico(1);
  };

  const handleLimparFiltros = () => {
    setDataInicio('');
    setDataFim('');
    setFiltroStatus('todos');
    setTimeout(() => buscarHistorico(1), 0);
  };

  const historicoFiltrado = historico.filter((item) => {
    if (filtroStatus === 'sucesso') return !item.erro && item.dataFim != null;
    if (filtroStatus === 'erro') return item.erro;
    return true;
  });

  // O banner de "em execução" deve ser baseado no statusAtual (retorno da API),
  // não no status da lista (que pode estar desatualizado).
  // Um job está em execução apenas quando há uma execução atual sem data de fim E sem cancelamento.
  const jobEmExecucao = statusAtual != null && !statusAtual.dataFim && !isCanceled(statusAtual);
  const ultimoLog = statusAtual?.logs?.length
    ? statusAtual.logs[statusAtual.logs.length - 1]
    : null;
  const percentualAtual = ultimoLog?.percentual ?? 0;

  const renderStatusBadge = (item) => {
    // Job cancelado: DataFim=null mas tem log de cancelamento
    if (item.dataFim == null && isCanceled(item)) {
      return (
        <S.StatusBadge canceled>
          <S.StatusBadgeIcon><UnCheckIcon width="14px" height="14px" color="#c3913b" /></S.StatusBadgeIcon>
          {intl.get('master-data.configurations.integrations.canceled') || 'Cancelado'}
        </S.StatusBadge>
      );
    }
    // Job realmente em execução: sem data de fim E é o job atual confirmado pela API de status
    if (item.dataFim == null && statusAtual != null && item.id === statusAtual.id) {
      return (
        <S.StatusBadge running>
          <S.StatusBadgeIcon><IconRefresh width={14} height={14} /></S.StatusBadgeIcon>
          {intl.get('master-data.configurations.integrations.running')}
        </S.StatusBadge>
      );
    }
    // DataFim=null mas não é o job atual — registro legado sem data de fim preenchida
    if (item.dataFim == null) {
      return (
        <S.StatusBadge>
          <S.StatusBadgeIcon><UnCheckIcon width="14px" height="14px" color="#95a3b7" /></S.StatusBadgeIcon>
          {intl.get('master-data.configurations.integrations.incomplete') || 'Incompleto'}
        </S.StatusBadge>
      );
    }
    if (item.erro) {
      return (
        <S.StatusBadge erro>
          <S.StatusBadgeIcon><UnCheckIcon width="14px" height="14px" /></S.StatusBadgeIcon>
          {intl.get('master-data.configurations.integrations.error')}
        </S.StatusBadge>
      );
    }
    // Sucesso com warnings
    if (hasWarnLogs(item)) {
      return (
        <S.StatusBadge warn>
          <S.StatusBadgeIcon>⚠️</S.StatusBadgeIcon>
          {intl.get('master-data.configurations.integrations.success')}
        </S.StatusBadge>
      );
    }
    return (
      <S.StatusBadge>
        <S.StatusBadgeIcon><CheckIcon width="14px" height="14px" /></S.StatusBadgeIcon>
        {intl.get('master-data.configurations.integrations.success')}
      </S.StatusBadge>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <S.DialogHeader>
        <S.DialogTitle>
          {intl.get('master-data.configurations.integrations.history')} — {descricaoTipoIntegracao}
        </S.DialogTitle>
        <S.CloseButton onClick={onClose}><CloseIcon /></S.CloseButton>
      </S.DialogHeader>

      <StyledDialogContent>
        {/* Status atual / job em execução */}
        {jobEmExecucao && (
          <S.RunningJobBanner>
            <S.RunningJobInfo>
              <S.RunningDot />
              <span>{intl.get('master-data.configurations.integrations.running')}</span>
              {statusAtual?.dataInicio && (
                <S.RunningStartTime>
                  {intl.get('master-data.configurations.integrations.startDate')}: {moment(statusAtual.dataInicio).format('L LT')}
                </S.RunningStartTime>
              )}
              {percentualAtual > 0 && (
                <S.ProgressWrapper>
                  <LoadingCircularProgress isLoading percentual={percentualAtual} />
                </S.ProgressWrapper>
              )}
              {ultimoLog?.mensagem && (
                <S.LiveLogMessage title={ultimoLog.mensagem}>
                  {ultimoLog.mensagem}
                </S.LiveLogMessage>
              )}
            </S.RunningJobInfo>
            {isAdmin && (
              <Button
                buttonType="default"
                value={cancelando
                  ? intl.get('master-data.configurations.integrations.canceling')
                  : intl.get('master-data.configurations.integrations.cancel')}
                onClick={handleCancelar}
                disabled={cancelando}
                style={{ width: '120px' }}
              />
            )}
          </S.RunningJobBanner>
        )}

        {/* Filtros */}
        <S.FiltersRow>
          <S.FilterItem>
            <DateInput
              label={intl.get('master-data.configurations.integrations.startDate')}
              value={dataInicio}
              onChange={(v) => setDataInicio(v || '')}
              canClear
            />
          </S.FilterItem>
          <S.FilterItem>
            <DateInput
              label={intl.get('master-data.configurations.integrations.endDate')}
              value={dataFim}
              onChange={(v) => setDataFim(v || '')}
              canClear
            />
          </S.FilterItem>
          <S.FilterItem>
            <S.StatusFilterLabel>{intl.get('master-data.configurations.integrations.status')}</S.StatusFilterLabel>
            <S.StatusFilterSelect
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <option value="todos">{intl.get('master-data.configurations.integrations.allStatuses')}</option>
              <option value="sucesso">{intl.get('master-data.configurations.integrations.successOnly')}</option>
              <option value="erro">{intl.get('master-data.configurations.integrations.errorOnly')}</option>
            </S.StatusFilterSelect>
          </S.FilterItem>
          <S.FilterActions>
            <Button
              buttonType="primary"
              value={intl.get('geral.buttonsDefault.search')}
              onClick={handleFiltrar}
              style={{ width: '100px' }}
            />
            <Button
              buttonType="default"
              value={intl.get('geral.buttonsDefault.clear')}
              onClick={handleLimparFiltros}
              style={{ width: '100px', marginLeft: '8px' }}
            />
          </S.FilterActions>
        </S.FiltersRow>

        {/* Tabela */}
        {isFetching ? (
          <S.LoadingWrapper>
            <LoadingCircularProgress isLoading percentual={0} />
          </S.LoadingWrapper>
        ) : (
          <>
            <S.TableHeader>
              <S.ColStart>{intl.get('master-data.configurations.integrations.startDate')}</S.ColStart>
              <S.ColEnd>{intl.get('master-data.configurations.integrations.endDate')}</S.ColEnd>
              <S.ColDuration>{intl.get('master-data.configurations.integrations.duration')}</S.ColDuration>
              <S.ColStatus>{intl.get('master-data.configurations.integrations.status')}</S.ColStatus>
              <S.ColExpand />
            </S.TableHeader>

            {historicoFiltrado.length === 0 && (
              <S.EmptyMessage>{intl.get('master-data.configurations.integrations.noHistory')}</S.EmptyMessage>
            )}

            {historicoFiltrado.map((item) => (
              <S.ExecRow key={item.id} erro={item.erro} running={item.dataFim == null && statusAtual != null && item.id === statusAtual.id}>
                <S.RowMain onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}>
                  <S.ColStart>
                    {item.dataInicio ? moment(item.dataInicio).format('L LT') : '-'}
                  </S.ColStart>
                  <S.ColEnd>
                    {item.dataFim ? moment(item.dataFim).format('L LT') : '-'}
                  </S.ColEnd>
                  <S.ColDuration>{formatDuration(item.duracaoMs)}</S.ColDuration>
                  <S.ColStatus>
                    {renderStatusBadge(item)}
                  </S.ColStatus>
                  <S.ColExpand>
                    {(item.erro || (item.logs && item.logs.length > 0)) && (
                      <S.ExpandIcon expanded={expandedId === item.id}>▼</S.ExpandIcon>
                    )}
                  </S.ColExpand>
                </S.RowMain>

                {expandedId === item.id && (
                  <S.RowDetail>
                    {item.erro && (
                      <S.ErrorSection>
                        <S.ErrorTitle>{intl.get('master-data.configurations.integrations.errorMessage')}</S.ErrorTitle>
                        <S.ErrorText>{item.mensagemErro || '-'}</S.ErrorText>
                        {item.stackTrace && (
                          <>
                            <S.ErrorTitle>{intl.get('master-data.configurations.integrations.stackTrace')}</S.ErrorTitle>
                            <S.StackTrace>{item.stackTrace}</S.StackTrace>
                          </>
                        )}
                      </S.ErrorSection>
                    )}
                    {item.logs && item.logs.length > 0 && (
                      <S.LogsSection>
                        <S.LogsTitle>{intl.get('master-data.configurations.integrations.progressLogs')}</S.LogsTitle>
                        {item.logs.map((log) => (
                          <S.LogRow key={log.id}>
                            <S.LogDate>{log.data ? moment(log.data).format('LT') : '-'}</S.LogDate>
                            <S.LogLevel nivel={log.nivel}>{log.nivel}</S.LogLevel>
                            <S.LogMessage>{log.mensagem}</S.LogMessage>
                            {log.percentual != null && (
                              <S.LogPercent>{log.percentual}%</S.LogPercent>
                            )}
                          </S.LogRow>
                        ))}
                      </S.LogsSection>
                    )}
                  </S.RowDetail>
                )}
              </S.ExecRow>
            ))}

            {totalRegistros > PAGE_SIZE && (
              <S.PaginationWrapper>
                <Pagination
                  page={pagina - 1}
                  pageSize={PAGE_SIZE}
                  amount={totalRegistros}
                  refreshSearch={(page) => buscarHistorico(page + 1)}
                />
              </S.PaginationWrapper>
            )}
          </>
        )}
      </StyledDialogContent>

      <S.DialogFooter>
        <Button
          buttonType="primary"
          value={intl.get('geral.buttonsDefault.close')}
          onClick={onClose}
          style={{ width: '120px' }}
        />
      </S.DialogFooter>
    </Dialog>
  );
};

ModalHistoricoExecucoes.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  integracao: PropTypes.object,
  onFeedback: PropTypes.func.isRequired,
};

export default ModalHistoricoExecucoes;
